// RateCard — the contract's standing product/services catalogue (item,
// description, unit, unit price or "— variable", GST). A first-class
// contract-scoped surface (mounted as its own Finance-tab sub-view), not a
// modal — every row here is a billable line an invoice charge or PO line can
// point at.
//
// Loads the finance-setup singleton via useFinanceSetup and saves through the
// usePutFinanceSetup full-replace mutation. RateCard is the only UI surface
// that writes this record, but the PUT body is the whole record, so a save
// preserves the loaded `financeEmail` verbatim (there is no UI to edit it —
// finance email isn't captured or sent at the moment) rather than clobbering
// it with an empty string alongside the serialised rateCard array.
// Presentational state lives over the pure rateCardState leaf, whose
// serialiser preserves existing row ids and mints ids for new rows (the PUT
// schema requires an id on every rate-card row).

import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import Button from "../../common/Button";
import Alert from "../../common/Alert";
import CurrencyInput from "../../common/CurrencyInput";
import LoadingSpinner from "../../common/LoadingSpinner";
import { useFinanceSetup, usePutFinanceSetup } from "../../../hooks/useFinance";
import { isConflictError } from "../../../services/api";
import {
  stateFromRateCard,
  addRateItem,
  removeRateItem,
  patchRateItem,
  validateRateCard,
  hasRateCardErrors,
  serialiseRateCard,
  type RateItemDraft,
} from "./rateCardState";

interface RateCardProps {
  contractId: string;
}

export default function RateCard({ contractId }: RateCardProps) {
  const setupQuery = useFinanceSetup(contractId);
  const putSetup = usePutFinanceSetup();

  const [drafts, setDrafts] = useState<RateItemDraft[]>([]);
  const [attempted, setAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Seed once the setup singleton has actually LOADED. `financeEmail` is not
  // edited here but must be preserved on save, so it is read straight off the
  // query result rather than mirrored into local state. Gate on `data != null`,
  // NOT merely `!isPending`: a failed load also settles `isPending=false` but with
  // `data=undefined`; seeding from that would produce an empty draft list and
  // let Save PUT `{ financeEmail: "", rateCard: [] }`, wiping the whole record.
  // `{ setup: null }` (a genuinely new contract) is a valid loaded state and
  // still seeds an empty editable card.
  const [seeded, setSeeded] = useState(false);
  if (!seeded && setupQuery.data != null) {
    setSeeded(true);
    setDrafts(stateFromRateCard(setupQuery.data.setup?.rateCard ?? []));
    setAttempted(false);
    setSubmitError(null);
  }

  const errors = validateRateCard(drafts);
  const isSubmitting = putSetup.isPending;
  // The setup record failed to load — its rateCard/financeEmail are unknown,
  // so a full-replace PUT can only destroy data. Block saving entirely.
  const loadFailed = !!setupQuery.error;

  const handleSave = async () => {
    // Belt-and-braces: never PUT a preserved field sourced from undefined data.
    if (loadFailed || setupQuery.data == null) return;
    setAttempted(true);
    if (hasRateCardErrors(errors)) return;
    setSubmitError(null);
    try {
      await putSetup.mutateAsync({
        contractId,
        input: {
          financeEmail: setupQuery.data?.setup?.financeEmail ?? "",
          rateCard: serialiseRateCard(drafts),
        },
      });
    } catch (err) {
      if (isConflictError(err)) {
        setSubmitError("The rate card changed elsewhere — reopen and try again.");
      } else {
        setSubmitError(err instanceof Error ? err.message : "Failed to save the rate card.");
      }
    }
  };

  return (
    <div className="card space-y-4 p-5" data-testid="finance-rate-card-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-h3 text-slate-900 dark:text-slate-100">Rate card</h3>
          <p className="text-body-sm text-slate-500 dark:text-slate-400">
            The contract&apos;s standing products and services. Point PO lines and
            invoice charges at these items.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          icon={<Plus className="h-4 w-4" aria-hidden="true" />}
          onClick={() => setDrafts((prev) => addRateItem(prev))}
          disabled={isSubmitting || setupQuery.isPending || loadFailed}
        >
          Add rate item
        </Button>
      </div>

      {setupQuery.isPending ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : setupQuery.error ? (
        <Alert variant="error">Failed to load the rate card.</Alert>
      ) : drafts.length === 0 ? (
        <p className="text-body-sm text-slate-500 dark:text-slate-400">
          No rate items yet — add your contract&apos;s products/services and rates.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="min-w-full border-collapse text-body-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr className="text-left text-overline uppercase text-slate-500 dark:text-slate-400">
                <th scope="col" className="px-3 py-2 font-medium">Item</th>
                <th scope="col" className="px-3 py-2 font-medium">Description</th>
                <th scope="col" className="px-3 py-2 font-medium">Unit</th>
                <th scope="col" className="px-3 py-2 font-medium">Unit price</th>
                <th scope="col" className="px-3 py-2 font-medium">GST</th>
                <th scope="col" className="px-3 py-2 font-medium">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody className="[&>tr:not(:first-child)]:border-t [&>tr:not(:first-child)]:border-t-slate-200 dark:[&>tr:not(:first-child)]:border-t-slate-700">
              {drafts.map((row, index) => (
                <tr key={row.key} className="align-top">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className="input-sm w-full"
                      aria-label="Rate item"
                      placeholder="Item"
                      value={row.item}
                      onChange={(e) =>
                        setDrafts((prev) => patchRateItem(prev, index, { item: e.target.value }))
                      }
                      disabled={isSubmitting}
                      aria-invalid={attempted && !!errors[row.key]}
                    />
                    {attempted && errors[row.key] && (
                      <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                        {errors[row.key]}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className="input-sm w-full"
                      aria-label="Description"
                      placeholder="Description (optional)"
                      value={row.description}
                      onChange={(e) =>
                        setDrafts((prev) =>
                          patchRateItem(prev, index, { description: e.target.value }),
                        )
                      }
                      disabled={isSubmitting}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className="input-sm w-full"
                      aria-label="Unit"
                      placeholder="Unit (site, month…)"
                      value={row.unit}
                      onChange={(e) =>
                        setDrafts((prev) => patchRateItem(prev, index, { unit: e.target.value }))
                      }
                      disabled={isSubmitting}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <CurrencyInput
                      size="sm"
                      className="w-full"
                      ariaLabel="Unit price"
                      placeholder="— variable"
                      value={row.unitPrice}
                      onChange={(raw) =>
                        setDrafts((prev) => patchRateItem(prev, index, { unitPrice: raw }))
                      }
                      disabled={isSubmitting}
                    />
                    <p className="mt-1 text-caption text-slate-400 dark:text-slate-500">
                      Blank = variable (priced per invoice)
                    </p>
                  </td>
                  <td className="px-3 py-2">
                    <label className="flex items-center gap-1.5 text-caption text-slate-600 dark:text-slate-400">
                      <input
                        type="checkbox"
                        aria-label="GST applies"
                        className="h-3.5 w-3.5 rounded-sm border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-900"
                        checked={row.gstApplies}
                        onChange={(e) =>
                          setDrafts((prev) =>
                            patchRateItem(prev, index, { gstApplies: e.target.checked }),
                          )
                        }
                        disabled={isSubmitting}
                      />
                      GST
                    </label>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconOnly
                      icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                      aria-label="Remove rate item"
                      onClick={() => setDrafts((prev) => removeRateItem(prev, index))}
                      disabled={isSubmitting}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {submitError && <Alert variant="error">{submitError}</Alert>}

      <div className="flex justify-end pt-1">
        <Button
          type="button"
          onClick={handleSave}
          loading={isSubmitting}
          disabled={setupQuery.isPending || loadFailed}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
